import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CLIENT_TYPE_LABELS, type ClientStatus, type ClientType } from "@shared/modules/x-atelier";
import { Badge } from "../../../core/components/ui/badge";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Input } from "../../../core/components/ui/input";
import { PageHeader } from "../../../core/components/ui/page-header";
import { NewClientBadge } from "../components/booking-badges";
import { ClientFormModal } from "../components/client-form-modal";
import { QuickActions } from "../components/quick-actions";
import { listBlockedNumbers, listClients, phoneKey, type ClientRow } from "../lib/api";
import { formatDayShort } from "../lib/dates";

/** Nouveau : aucun travail terminé ; confirmé : au moins un. */
const STATUSES: Array<{ key: ClientStatus; label: string }> = [
  { key: "nouveau", label: "Nouveaux clients" },
  { key: "confirme", label: "Clients confirmés" },
];

const TYPES: Array<{ key: "" | ClientType; label: string }> = [
  { key: "", label: "Tous" },
  ...(Object.entries(CLIENT_TYPE_LABELS) as Array<[ClientType, string]>).map(([key, label]) => ({
    key,
    label,
  })),
];

/** Initiales d'un nom (« Marie Tremblay » → « MT »). */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");
}

/** Liste des clients : recherche, filtre par type en pastilles, rangées tactiles avec appel direct. */
export function ClientsPage() {
  const navigate = useNavigate();
  // Type et recherche vivent dans l'URL : « Retour » depuis une fiche client
  // retrouve la liste filtrée et sa position.
  const [searchParams, setSearchParams] = useSearchParams();
  const type = (searchParams.get("type") ?? "") as "" | ClientType;
  const setType = (value: "" | ClientType) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("type", value);
        else next.delete("type");
        return next;
      },
      { replace: true },
    );
  // Nouveaux (aucun travail terminé) ou confirmés ; vide = tous.
  const statut = (searchParams.get("statut") ?? "") as "" | ClientStatus;
  const setStatut = (value: "" | ClientStatus) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("statut", value);
        else next.delete("statut");
        return next;
      },
      { replace: true },
    );
  const [query, setQuery] = useState(() => searchParams.get("q") ?? "");
  const [debounced, setDebounced] = useState(() => (searchParams.get("q") ?? "").trim());
  const [rows, setRows] = useState<ClientRow[]>([]);
  // Numéros de la liste noire (dix derniers chiffres), pour la pastille.
  const [blockedKeys, setBlockedKeys] = useState<Set<string>>(new Set());
  useEffect(() => {
    listBlockedNumbers()
      .then((list) =>
        setBlockedKeys(
          new Set(list.map((b) => phoneKey(b.phone)).filter((k): k is string => Boolean(k))),
        ),
      )
      .catch(() => setBlockedKeys(new Set()));
  }, []);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const urlQuery = searchParams.get("q") ?? "";
  useEffect(() => {
    if (urlQuery === debounced) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debounced) next.set("q", debounced);
        else next.delete("q");
        return next;
      },
      { replace: true },
    );
    // L’URL suit la saisie, pas l’inverse.
  }, [debounced]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listClients(debounced || undefined, type || undefined, statut || undefined)
      .then((list) => {
        if (cancelled) return;
        setRows(list);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, type, statut]);

  return (
    <div className="page clients">
      <PageHeader
        title="Clients"
        subtitle="Particuliers, concessionnaires et flottes, avec leurs véhicules."
        actions={
          <Button
            variant="primary"
            icon={<Icon name="plus" size={18} />}
            onClick={() => setCreating(true)}
          >
            Nouveau client
          </Button>
        }
      />

      <div className="chips chips--tabs" role="group" aria-label="Type de client">
        {TYPES.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`chips__chip${type === t.key ? " is-active" : ""}`}
            aria-pressed={type === t.key}
            onClick={() => setType(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="atelier-toolbar">
        <div className="atelier-toolbar__search">
          <Input
            type="search"
            aria-label="Rechercher un client"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Nom, téléphone, courriel, immatriculation, NIV…"
            enterKeyHint="search"
          />
        </div>
        <select
          className="select clients__status"
          aria-label="Statut du client"
          value={statut}
          onChange={(e) => setStatut(e.target.value as "" | ClientStatus)}
        >
          <option value="">Tous les statuts</option>
          {STATUSES.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
        <div className="atelier-toolbar__spacer" />
        <span className="atelier-toolbar__count">
          {loading ? "…" : `${rows.length} client${rows.length > 1 ? "s" : ""}`}
        </span>
      </div>

      {!loading && rows.length === 0 ? (
        <p className="empty">
          {failed
            ? "Liste indisponible — réessayez dans un instant."
            : debounced || type || statut
              ? "Aucun client ne correspond."
              : "Aucun client pour l'instant."}
        </p>
      ) : loading && rows.length === 0 ? (
        <p className="empty">Chargement…</p>
      ) : (
        <ul className="client-list">
          {rows.map((c) => (
            <li key={c.id} className="client-row">
              <Link to={`/clients/${c.id}`} className="client-row__main">
                <span
                  className={`client-row__avatar client-row__avatar--${c.type}`}
                  aria-hidden="true"
                >
                  {initials(c.name)}
                </span>
                <span className="client-row__text">
                  <span className="client-row__name">
                    {c.name} <NewClientBadge status={c.status} compact />
                    {phoneKey(c.phone) && blockedKeys.has(phoneKey(c.phone)!) ? (
                      <Badge variant="danger">Bloqué</Badge>
                    ) : null}
                  </span>
                  <span className="client-row__sub">
                    {c.phone ?? "Sans téléphone"} · {c.vehicleCount} véhicule
                    {c.vehicleCount > 1 ? "s" : ""}
                    {c.lastAppointmentAt
                      ? ` · dernier RDV ${formatDayShort(c.lastAppointmentAt)}`
                      : ""}
                  </span>
                </span>
                <span className="client-row__type">
                  <Badge variant={c.type === "particulier" ? "neutral" : "primary"}>
                    {CLIENT_TYPE_LABELS[c.type]}
                  </Badge>
                </span>
              </Link>
              <span className="client-row__actions">
                <QuickActions phone={c.phone} size="sm" />
                <Button
                  variant="ghost"
                  size="sm"
                  className="btn--icon"
                  aria-label={`Ouvrir ${c.name}`}
                  icon={<Icon name="chevron-right" size={18} />}
                  onClick={() => navigate(`/clients/${c.id}`)}
                />
              </span>
            </li>
          ))}
        </ul>
      )}

      <ClientFormModal
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={(c) => {
          setCreating(false);
          navigate(`/clients/${c.id}`);
        }}
      />
    </div>
  );
}
